import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cogoToast from 'cogo-toast';
import { getOnboardingBrands, publishOnboardingBrands, searchFeaturedBrands } from '../../api/circles';
import { prettifyImage } from '../../api/images';
import { getSmartImage } from '../../helpers/helpers';
import OrderedRows from './OrderedRows';
import Modal from '../General/Modal';
import ShopmyImage from '../General/ShopmyImage';
import ImageField from './ImageField';
import FeaturedBrandArtwork from './FeaturedBrandArtwork';
import { message, confirmDiscard } from './circle_helpers';

const values = rows =>
  rows.map(row => ({ AllBrand_id: row.brand.id, imageOverride: row.imageOverride || null, imageOverrideNonText: row.imageOverrideNonText || null }));
const draftRows = features => features.map(row => ({ ...row, id: row.brand.id, title: row.brand.name }));

// Keep this mounted when changing tabs so an unpublished brand list stays intact.
export default function FeaturedBrands({ visible, onDirtyChange }) {
  const [saved, setSaved] = useState(null);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState({ brands: [], loading: false, error: '' });
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [editingLogoId, setEditingLogoId] = useState(null);
  const [editingCoverId, setEditingCoverId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [removingBackground, setRemovingBackground] = useState(false);
  const [logoUndo, setLogoUndo] = useState(null);
  const [logoError, setLogoError] = useState('');
  const logoBusy = uploading || removingBackground;
  const editingLogo = rows.find(row => row.id === editingLogoId);
  const editingCover = rows.find(row => row.id === editingCoverId);
  const coverImage = editingCover && (editingCover.imageOverrideNonText || editingCover.brand.coverImage);
  const closeCover = () => !uploading && setEditingCoverId(null);
  const closeLogo = () => !logoBusy && setEditingLogoId(null);
  const dirty = !!saved && JSON.stringify(values(rows)) !== JSON.stringify(values(saved.features));
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => {
    if (!visible || saved) return;
    let active = true;
    setError('');
    getOnboardingBrands()
      .then(data => {
        if (!active) return;
        setSaved(data);
        setRows(draftRows(data.features));
      })
      .catch(error => active && setError(message(error)));
    return () => {
      active = false;
    };
  }, [visible, saved, attempt]);
  useEffect(() => {
    let active = true;
    setSearch({ brands: [], loading: query.trim().length >= 2, error: '' });
    if (query.trim().length < 2) return;
    const timer = setTimeout(() => {
      searchFeaturedBrands(query)
        .then(data => active && setSearch({ brands: data.brands, loading: false, error: '' }))
        .catch(error => active && setSearch({ brands: [], loading: false, error: message(error) }));
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query]);
  const publish = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await publishOnboardingBrands(values(rows), saved.revision);
      setSaved(data);
      setRows(draftRows(data.features));
      cogoToast.success('Onboarding brands published');
    } catch (error) {
      const failure = error.response?.data;
      if (failure?.code === 'ONBOARDING_BRANDS_UNAVAILABLE' && Array.isArray(failure.brands)) {
        const unavailableIds = failure.brands.map(brand => brand.id);
        setRows(old => old.map(row => (unavailableIds.includes(row.brand.id) ? { ...row, available: false } : row)));
      }
      setError(message(error));
    } finally {
      setBusy(false);
    }
  };
  const changeLogo = imageOverride => {
    setRows(old => old.map(row => (row.id === editingLogo.id ? { ...row, imageOverride } : row)));
  };
  const removeBackground = async () => {
    if (logoBusy) return;
    setRemovingBackground(true);
    setLogoError('');
    try {
      const result = await prettifyImage(getSmartImage(editingLogo.imageOverride || editingLogo.brand.logo), { removeBackgroundOnly: true });
      if (!result.url) throw new Error('No image returned');
      setLogoUndo({ imageOverride: editingLogo.imageOverride || null });
      changeLogo(result.url);
    } catch {
      setLogoError('Could not remove the background. Your logo is unchanged. Please try again.');
    } finally {
      setRemovingBackground(false);
    }
  };
  return (
    <section className='featured-brands circle-featured-placement' hidden={!visible} aria-label='Onboarding brands'>
      <label>
        Add a brand
        <input value={query} disabled={busy || !saved} placeholder='Search by brand name' onChange={event => setQuery(event.target.value)} />
      </label>
      <p className='circle-category-hint'>Choose individual brands, excluding multi-brand retailers. Changes save when you publish brands.</p>
      {search.loading && <p role='status'>Searching brands…</p>}
      {search.error && <p role='alert'>{search.error}</p>}
      {query.trim().length >= 2 && !search.loading && !search.error && !search.brands.length && <p>No brands found.</p>}
      {!!search.brands.length && (
        <div className='circle-search-results'>
          {search.brands.map(brand => (
            <div className='circle-row' key={brand.id}>
              <ShopmyImage className='circle-thumbnail featured-brand-logo' source={brand.logo} alt='' />
              <strong>{brand.name}</strong>
              <button
                disabled={busy || !saved || rows.length >= saved.limit || rows.some(row => row.brand.id === brand.id)}
                onClick={() => setRows(old => [...old, { id: brand.id, title: brand.name, brand, available: true }])}
              >
                {rows.some(row => row.brand.id === brand.id) ? 'Added' : 'Add brand'}
              </button>
            </div>
          ))}
        </div>
      )}
      <div className='circle-placement-heading'>
        <div>
          <h2>Onboarding</h2>
          <p>Brands offered in the onboarding brand picker.</p>
        </div>
        {saved && (
          <span className='circle-placement-count'>
            {rows.length} / {saved.limit} Brands
          </span>
        )}
      </div>
      {error && (
        <p role='alert'>
          {error}{' '}
          <button
            disabled={busy}
            onClick={() => {
              const reload = () => {
                setSaved(null);
                setAttempt(value => value + 1);
              };
              if (dirty) confirmDiscard('Reload the published brand list and discard your changes?', reload);
              else reload();
            }}
          >
            Reload brands
          </button>
        </p>
      )}
      {!saved ? (
        <p>Loading brands…</p>
      ) : (
        <>
          <OrderedRows
            emptyMessage='No brands added yet.'
            rows={rows}
            onChange={setRows}
            onRemove={ids => setRows(old => old.filter(row => !ids.includes(row.id)))}
            disabled={busy}
            renderDragPreview={moving => (
              <span>
                {moving[0].brand.name}
                {moving.length > 1 ? ` +${moving.length - 1} more` : ''}
              </span>
            )}
            header={
              <div className='circle-placement-columns'>
                <span>Display Rank</span>
                <div className='circle-placement-row-columns'>
                  <span>Brand</span>
                </div>
              </div>
            }
            renderRow={row => (
              <>
                <FeaturedBrandArtwork
                  className='circle-thumbnail'
                  logo={row.imageOverride || row.brand.logo}
                  coverImage={row.imageOverrideNonText || row.brand.coverImage}
                />
                <div className='circle-info'>
                  <strong>{row.brand.name}</strong>
                  {row.available === false && <p>Unavailable — remove before publishing</p>}
                </div>
                <div className='circle-actions'>
                  <button
                    disabled={busy}
                    aria-label={`Edit logo for ${row.brand.name}`}
                    onClick={() => {
                      setLogoUndo(null);
                      setLogoError('');
                      setEditingLogoId(row.id);
                    }}
                  >
                    Edit logo
                  </button>
                  <button disabled={busy} aria-label={`Edit cover for ${row.brand.name}`} onClick={() => setEditingCoverId(row.id)}>
                    Edit cover
                  </button>
                  <button
                    disabled={busy}
                    aria-label={`Remove ${row.brand.name}`}
                    onClick={() => setRows(old => old.filter(item => item.id !== row.id))}
                  >
                    Remove
                  </button>
                </div>
              </>
            )}
          />
          <div className='circle-toolbar circle-placement-footer'>
            <span className={`circle-placement-status ${dirty ? 'pending' : ''}`}>
              {rows.length} / {saved.limit} brands · {dirty ? 'Unpublished changes' : 'Published'}
            </span>
            <button
              disabled={!dirty || busy}
              onClick={() => confirmDiscard('Discard your unpublished brand changes?', () => setRows(draftRows(saved.features)))}
            >
              Discard brand changes
            </button>
            <button className='primary' disabled={!dirty || busy || rows.some(row => row.available === false)} onClick={publish}>
              {busy ? 'Publishing…' : 'Publish brands'}
            </button>
          </div>
          {editingCover && (
            <Modal close={closeCover} container_class='circle-editor featured-brand-cover-editor' focusTrap labelledBy='featured-brand-cover-title'>
              <header className='circle-toolbar'>
                <h2 id='featured-brand-cover-title'>Onboarding cover for {editingCover.brand.name}</h2>
                <button disabled={uploading} onClick={closeCover}>
                  Done
                </button>
              </header>
              <p>Choose a cover image just for onboarding. It will appear behind the logo in the square brand tile.</p>
              <FeaturedBrandArtwork
                size={180}
                name={editingCover.brand.name}
                logo={editingCover.imageOverride || editingCover.brand.logo}
                coverImage={coverImage}
              />
              <fieldset disabled={uploading}>
                <ImageField
                  label='Cover image override'
                  value={editingCover.imageOverrideNonText}
                  onUploading={setUploading}
                  onChange={imageOverrideNonText =>
                    setRows(old => old.map(row => (row.id === editingCover.id ? { ...row, imageOverrideNonText } : row)))
                  }
                />
              </fieldset>
              <p>
                {editingCover.imageOverrideNonText
                  ? 'Using an onboarding-only cover. Remove the image to use the brand’s regular cover.'
                  : coverImage
                    ? 'Using the brand’s regular cover.'
                    : 'This brand has no cover image. The tile will show its logo on white.'}
              </p>
              <p>Publish brands to save your changes.</p>
            </Modal>
          )}
          {editingLogo && (
            <Modal close={closeLogo} container_class='circle-editor featured-brand-logo-editor' focusTrap labelledBy='featured-brand-logo-title'>
              <header className='circle-toolbar'>
                <h2 id='featured-brand-logo-title'>Onboarding logo for {editingLogo.brand.name}</h2>
                <button disabled={logoBusy} onClick={closeLogo}>
                  Done
                </button>
              </header>
              <p>This logo is used only during onboarding. The brand’s global logo stays unchanged.</p>
              <ShopmyImage
                className='featured-brand-logo-preview'
                source={editingLogo.imageOverride || editingLogo.brand.logo}
                alt={`${editingLogo.brand.name} onboarding logo preview`}
              />
              <fieldset disabled={logoBusy}>
                <ImageField
                  label='Logo override'
                  value={editingLogo.imageOverride}
                  onUploading={setUploading}
                  onChange={imageOverride => {
                    changeLogo(imageOverride);
                    setLogoUndo(null);
                    setLogoError('');
                  }}
                />
                <div className='circle-actions'>
                  <button disabled={!editingLogo.imageOverride && !editingLogo.brand.logo} onClick={removeBackground}>
                    {removingBackground ? 'Removing background…' : 'Remove background'}
                  </button>
                  {logoUndo && (
                    <button
                      onClick={() => {
                        changeLogo(logoUndo.imageOverride);
                        setLogoUndo(null);
                        setLogoError('');
                      }}
                    >
                      Undo background removal
                    </button>
                  )}
                </div>
              </fieldset>
              {removingBackground && <p role='status'>Removing background…</p>}
              {logoError && <p role='alert'>{logoError}</p>}
              <p>Check the preview for missing lettering before publishing. The checkerboard shows transparency.</p>
              <p>Remove the override to use the original brand logo. Publish brands to save your changes.</p>
            </Modal>
          )}
        </>
      )}
    </section>
  );
}

FeaturedBrands.propTypes = { visible: PropTypes.bool.isRequired, onDirtyChange: PropTypes.func.isRequired };
